/**
 * Shared helpers for effective-dated records (rules page "Effective-Dated Records").
 *
 * - `effective_to` is inclusive; null/empty means open-ended.
 * - Dates are calendar days: never parse "YYYY-MM-DD" with `new Date(str)`, which is UTC
 *   and shifts the day backwards in US time zones.
 * - Status is the stored lifecycle; State (Lapsed / Current / Future) is computed against today.
 */

export const OPEN_ENDED_DASH = "—";
export const OPEN_ENDED_TOOLTIP = "Open-ended";

export const EFFECTIVE_FROM_LABEL = "Effective from";
export const EFFECTIVE_TO_LABEL = "Effective to";

export const EFFECTIVE_STATE = Object.freeze({
  LAPSED: "lapsed",
  CURRENT: "current",
  FUTURE: "future",
});

export const EFFECTIVE_STATE_LABELS = Object.freeze({
  lapsed: "Lapsed",
  current: "Current",
  future: "Future",
});

export const EFFECTIVE_STATUS_LABELS = Object.freeze({
  draft: "Draft",
  in_force: "In Force",
  relieved: "Relieved",
  terminated: "Terminated",
  proposed: "Proposed",
  voided: "Voided",
});

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/** Local-midnight Date for a "YYYY-MM-DD" string (or any Date/timestamp), else null. */
export function parseLocalDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === "string") {
    const iso = ISO_DAY.exec(value.trim());
    if (iso) {
      const [, year, month, day] = iso;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function isOpenEnded(effectiveTo) {
  return effectiveTo == null || effectiveTo === "";
}

/** Normalises a stored/label status ("IN_FORCE", "In Force") to its key ("in_force"). */
export function normalizeStatusKey(status) {
  return String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/** Computed State against `today` (inclusive end): "lapsed" | "current" | "future" | null. */
export function stateOf(effectiveFrom, effectiveTo, today = new Date()) {
  const from = parseLocalDate(effectiveFrom);
  const to = parseLocalDate(effectiveTo);
  const day = parseLocalDate(today);
  if (!day || (!from && !to)) return null;
  if (from && day < from) return EFFECTIVE_STATE.FUTURE;
  if (to && day > to) return EFFECTIVE_STATE.LAPSED;
  return EFFECTIVE_STATE.CURRENT;
}

/** Display label for a State key or an already-labelled value; "" when unknown. */
export function stateLabel(state) {
  const key = String(state ?? "").trim().toLowerCase();
  return EFFECTIVE_STATE_LABELS[key] || "";
}

/**
 * Display Status. An In Force record whose period has ended reads "Relieved"
 * (mirrors the API's display_status); every other status shows its own label.
 */
export function displayStatus(status, effectiveTo, today = new Date()) {
  const key = normalizeStatusKey(status);
  if (!key) return "";
  if (key === "in_force" && !isOpenEnded(effectiveTo)) {
    const to = parseLocalDate(effectiveTo);
    const day = parseLocalDate(today);
    if (to && day && day > to) return EFFECTIVE_STATUS_LABELS.relieved;
  }
  return EFFECTIVE_STATUS_LABELS[key] || "";
}
